import { message } from "antd";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { setAcoustiqueSolution, setEtudeData, setNoSolution, setSolutions } from "store/etudeSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { filterObject } from "utils";
import { HandleSubmit } from "utils/hooks/useCustomForm";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import useTranslation from "utils/hooks/useTranslation";
import { EtudeStep } from "utils/defines";
import {
  calcFieldsSchema,
  calcFieldTypes,
  EtudeFormFields,
} from "../constants";
import { useCurrentEtudeData } from "./useCurrentEtudeData";
import { useGetSolutionRequestBody } from "./useGetSolutionRequestBody";
import { InstallationType, useGetSolutionsQuery } from "./useGetSolutionsQuery";
import { boilerTypeArrayToApiNumber } from "utils/helpers/boilerType";

export const useGetSolutions = (
  handleSubmit?: HandleSubmit<EtudeFormFields>,
  hasEcs?: boolean,
  hasChauffage?: boolean,
  calculatedSolutions?: EtudeSolution | null,
  service?: string
) => {
  const dispatch = useAppDispatch();
  const { network } = useSalesNetwork();
  const { language } = useTranslation();
  const { etudeData, etudeId } = useCurrentEtudeData();
  const siteName = useAppSelector((state) => state.common.siteName);
  const { t } = useTranslation();
  const getRequestBody = useGetSolutionRequestBody(
    hasEcs,
    hasChauffage,
    calculatedSolutions
  );
  const { getElectricSolutions, getHybrideSolutions, isSolutionsFetching } =
    useGetSolutionsQuery();
  const navigate = useNavigate();
  const onSearchSolutions = useCallback(() => {
    if (language && network) {
      handleSubmit &&
        handleSubmit(async (values) => {
          const installationType: InstallationType =
            values.installationType as InstallationType;
          calcFieldsSchema.batiment.type =
            calcFieldTypes[
            values.subApplication as keyof typeof calcFieldTypes
            ];
          const requestBody = getRequestBody(
            values,
            filterObject(values, calcFieldsSchema as any, values.expertModeType)
              ?.batiment
          );
          if (requestBody) {
            try {
              const apiRequestBody: GetSolutionRequest = {
                ...requestBody,
                filtres: {
                  ...(requestBody.filtres || {}),
                  type_chaudiere: boilerTypeArrayToApiNumber(
                    requestBody.filtres?.type_chaudiere
                  ) as any,
                },
              };

              const result: GetSolutionResponse = await (installationType ===
                InstallationType.electric
                ? getElectricSolutions
                : getHybrideSolutions)(apiRequestBody).unwrap();

              if (!result.solutions && result?.detail?.[0]?.code != "2015") {
                dispatch(setNoSolution(true));
                throw Error(t(`ERR_MOTEUR_${result.detail?.[0]?.code}`));
              }

              dispatch(
                setEtudeData({
                  ...etudeData,
                  siteName: values.siteName || siteName || "",
                  siteCP: "",
                  siteCity: "",
                  clientName: "",
                  etudeId,
                  json: JSON.stringify({
                    ...values,
                    step: EtudeStep.Solutions,
                    stepName: t("FO_STUDIES_IN_PROGRESS"),
                    solutionRequestBody: requestBody,
                    installationType,
                    service,
                  }),
                })
              );
              dispatch(setSolutions(result.solutions ?? []));
              dispatch(setAcoustiqueSolution(result.acoustique_solution ?? false));
              navigate("solutions", {
                relative: "route",
              });
            } catch (err: any) {
              if (err.response?.status === 500) {
                throw new Error(t("ERR_MOTEUR_500"));
              }
              throw err;
            }
          }
          return Promise.resolve(false);
        })();
    }
  }, [language, network, calculatedSolutions, service, etudeId, etudeData]);

  const onFilterSolutions = useCallback(
    async (requestBody: GetSolutionRequest) => {
      if (language && network) {
        try {
          dispatch(
            setEtudeData({
              ...etudeData!,
              siteName:
                etudeData?.siteName ||
                JSON.parse(etudeData?.json || "{}").siteName ||
                "",
              json: JSON.stringify({
                ...JSON.parse(etudeData?.json || "{}"),
                solutionRequestBody: requestBody,
              }),
            })
          );
        } catch (err: any) {
          message.error(err?.data?.Message ?? err?.data ?? "error");
          console.log(err);
          return Promise.reject(err);
        }
      }
    },
    [language, network, etudeData]
  );

  return {
    isSolutionsFetching,
    onSearchSolutions,
    onFilterSolutions,
  };
};
